import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { WeezHeader, Button, FrameCard, ToastContext } from '@weezevent/nacre';
import { Form, Dropdown } from 'semantic-ui-react';
import { useForm, requiredField } from '../../../utils/validators';

import Widget from '../../../models/widget';

import WidgetTemplates from '../components/widget-templates/widget-templates';

export function Home({ title, subtitle, onSave, onCancel }) {
    const { t } = useTranslation();

    return (
        <WeezHeader
            backLink={{ onClick: onCancel }}
            title={title}
            rightComponent={[
                <Button primary key="primary" label={t('ticket.common.action.save')} onClick={onSave} />,
                <Button key="secondary" label={t('ticket.common.action.cancel')} onClick={onCancel} />
            ]}
        >
            {subtitle}
        </WeezHeader>
    );
}
Home.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default function FormWidgetSteps({ organizationId, path, widgetId, history }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            widget_key: requiredField
        };
    }, []);

    let [form, updateForm, setForm, formErrors, formValidate] = useForm(
        {
            name: '',
            widget_key: '',
            template: 'refund',
            widget_config: {}
        },
        validators
    );

    const onSave = React.useCallback(() => {
        if (!formValidate()) {
            return;
        }

        let formData = {
            organization: +organizationId,
            all_events: false,
            widget_events: [],
            events: [],
            default_language: null,
            languages: null,
            display_ended_events: false,
            customs: {},
            first_step: null,
            ...form
        };

        let widget = new Widget(formData);

        widget
            .save({ organizationId })
            .then(({ response }) => {
                toast.success(t(`ticket.widget.form.${widgetId ? 'successCreate' : 'successSave'}`));
                return history.replace(`/ticket/O${organizationId}/widgets/${response.id}/steps`);
            })
            .catch(e => {
                toast.error(t('ticket.widget.form.failCreate'));
            });
    });

    const onCancel = () => {
        return history.push(`/ticket/O${organizationId}/widgets`);
    };

    const templateOptions = [
        {
            key: 'refund',
            value: 'refund',
            text: t('ticket.widget.formStep.refund')
        },
        {
            key: 'basic',
            value: 'basic',
            text: t('ticket.widget.formStep.basic')
        },
        {
            key: 'manage_order_all_included',
            value: 'manage_order_all_included',
            text: t('ticket.widget.formStep.manage_order_all_included')
        }
    ];

    const Template = WidgetTemplates[form.template] || null;

    return (
        <div>
            <Home title={t('ticket.widget.formStep.title')} subtitle={t('ticket.widget.formStep.subtitle')} onSave={onSave} onCancel={onCancel} />
            <FrameCard title={t('ticket.widget.formStep.config_title')}>
                <Form>
                    <Form.Field error={formErrors['name'] ? true : false}>
                        <Form.Input label={t(`ticket.widget.formStep.name`)} required value={form.name} onChange={synth => updateForm('name', synth.target.value)} />
                        {formErrors['name'] ? (
                            <label>
                                <Trans i18nKey={formErrors['name']}>This field is required</Trans>{' '}
                            </label>
                        ) : null}
                    </Form.Field>

                    <Form.Field>
                        <label>
                            <Trans i18nKey={'ticket.widget.formStep.template'}>Template</Trans>{' '}
                        </label>
                        <Dropdown
                            fluid
                            selection
                            options={templateOptions}
                            onChange={(synth, data) => {
                                setForm(
                                    {
                                        template: data.value,
                                        widget_key: null
                                    },
                                    true
                                ); // true means "merge" both forms and do not replace exisitng keys
                            }}
                            value={form.template}
                        />
                    </Form.Field>

                    {Template && <Template form={form} onChange={updateForm} formErrors={formErrors} />}
                </Form>
            </FrameCard>
        </div>
    );
}

FormWidgetSteps.propTypes = {
    organizationId: PropTypes.number.isRequired,
    path: PropTypes.object.isRequired,
    widgetId: PropTypes.number,
    history: PropTypes.object.isRequired
};
